import React, { useState, useEffect } from "react";
import * as Dialog from '@radix-ui/react-dialog';
import { X, CalendarDays, BookOpen } from "lucide-react";
import { inquiryService } from "../../services/inquiryService";

const PropertyInquiryForm = ({ onClose, inquiryType }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const property_url = window.location.href;
      const property_id = "GodrejBarcaMSR";

      let finalMessage = formData.message;
      if (inquiryType === 'brochure') {
        finalMessage = finalMessage ? `Brochure Request: ${finalMessage}` : "Brochure Request";
      } else if (inquiryType === 'scheduleVisit') {
        finalMessage = finalMessage ? `Schedule Visit Request: ${finalMessage}` : "Schedule Visit Request";
      }

      await inquiryService.createPropertyInquiry({
        ...formData,
        message: finalMessage,
        property_url,
        property_id,
        status: 'new'
      });
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
      setTimeout(() => onClose(), 1000);
    } catch (error) {
      console.error('Error submitting property inquiry:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 space-y-4">
      {submitStatus === 'success' && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Success!</strong>
          <span className="block sm:inline"> Your inquiry has been submitted.</span>
        </div>
      )}
      {submitStatus === 'error' && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error!</strong>
          <span className="block sm:inline"> Failed to submit your inquiry. Please try again.</span>
        </div>
      )}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">Name</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:outline-none focus:ring-2 focus:ring-accent"
          placeholder="Your Full Name"
        />
      </div>
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:outline-none focus:ring-2 focus:ring-accent"
          placeholder="your.email@example.com"
        />
      </div>
      <div>
        <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Mobile No</label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:outline-none focus:ring-2 focus:ring-accent"
          placeholder="e.g., +91 98765 43210"
        />
      </div>
      <div>
        <label htmlFor="message" className="block text-sm font-medium text-gray-700">Message</label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows="4"
          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:outline-none focus:ring-2 focus:ring-accent"
          placeholder="Enter your message here (optional)"
        ></textarea>
      </div>
      <button
        type="submit"
        className="w-full bg-accent hover:bg-primary-700 text-white py-3 rounded-md font-semibold hover:bg-accent-dark transition-colors duration-200"
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Submitting...' : 'Submit'}
      </button>
    </form>
  );
};


export function MsrcityInquiryDialog({ isBrochureDialogOpen, setIsBrochureDialogOpen, isScheduleVisitDialogOpen, setIsScheduleVisitDialogOpen }) {
  // automatically open the 'Schedule a Visit'
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsScheduleVisitDialogOpen(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, [setIsScheduleVisitDialogOpen]);

  return (
    <>
      {/* Schedule a Visit Button (Right Side) */}
      <div className="fixed top-1/2 right-0 z-50 transform -translate-y-1/2 -rotate-90 origin-bottom-right">
        <Dialog.Root open={isScheduleVisitDialogOpen} onOpenChange={setIsScheduleVisitDialogOpen}>
          <Dialog.Trigger asChild>
            <button
              className="
                relative
                bg-accent
                text-white
                px-5
                py-2
                rounded-t-lg
                text-sm
                font-bold
                tracking-wide
                shadow-xl
                hover:shadow-2xl
                hover:bg-primary-700
                transition-all
                duration-400
                ease-in-out
                focus:outline-none
                cursor-pointer
              "
            >
              <span className="relative z-10 whitespace-nowrap flex items-center">
                <CalendarDays className="h-5 w-5 mr-2" /> SCHEDULE A VISIT
              </span>
            </button>
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Overlay
              className="
                fixed
                inset-0
                bg-black/40
                backdrop-blur-sm
                z-50
                flex
                items-center
                justify-center
                data-[state=open]:animate-overlayShow
                data-[state=closed]:animate-overlayHide
              "
            >
              <Dialog.Content
                className="
                  relative
                  bg-white
                  rounded-xl
                  shadow-2xl
                  w-[90%]
                  md:w-3/4
                  lg:w-2/3
                  max-w-4xl
                  mx-auto
                  data-[state=open]:animate-contentShow
                  data-[state=closed]:animate-contentHide
                "
              >
                <Dialog.Close asChild>
                  <button className="
                    absolute
                    top-1
                    right-1
                    md:top-4
                    md:right-4
                    text-gray-400
                    hover:text-gray-700
                    transition-colors
                    duration-200
                    focus:outline-none
                    p-1
                  ">
                    <X className="h-6 w-6" />
                  </button>
                </Dialog.Close>
                <div className="flex flex-col md:flex-row h-full">
                  <div className="md:w-1/2 md:block hidden overflow-hidden rounded-l-xl">
                    <img
                      src="https://1.bp.blogspot.com/-WAL3p1DzT20/XRHRd8as7iI/AAAAAAAAA1M/DWQb5G-WtCM0gfRt8PbSzQkdzO1LMr5dgCLcBGAs/s1600/azure-elevation-5349302.jpeg" // Dummy image URL
                      alt="Property Image"
                      className="object-cover w-full h-full"
                    />
                  </div>
                  <div className="md:w-1/2 flex flex-col justify-center p-6 rounded-r-xl">
                    <Dialog.Title className="text-xl md:text-2xl font-extrabold text-gray-900 mb-2 text-center leading-tight">
                      SCHEDULE A VISIT
                    </Dialog.Title>
                    <p className="text-gray-600 text-sm text-center mb-4">
                      Fill out the form to book your visit to Godrej Barca. Our
                      team will contact you shortly to confirm the details.
                    </p>
                    <PropertyInquiryForm onClose={() => setIsScheduleVisitDialogOpen(false)} inquiryType="scheduleVisit" />
                  </div>
                </div>
              </Dialog.Content>
            </Dialog.Overlay>
          </Dialog.Portal>
        </Dialog.Root>
      </div>

      {/* Brochure Button (Left Side) */}
      <div className="fixed top-1/2 left-0 z-50 transform -translate-y-1/2 rotate-90 origin-bottom-left">
        <Dialog.Root open={isBrochureDialogOpen} onOpenChange={setIsBrochureDialogOpen}>
          <Dialog.Trigger asChild>
            <button
              className="
                relative
                bg-accent
                text-white
                px-5
                py-2
                rounded-t-lg
                text-sm
                font-bold
                tracking-wide
                shadow-xl
                hover:shadow-2xl
                hover:bg-primary-700
                transition-all
                duration-400
                ease-in-out
                focus:outline-none
                cursor-pointer
              "
            >
              <span className="relative z-10 whitespace-nowrap flex items-center">
                <BookOpen className="h-5 w-5 mr-2" /> BROCHURE
              </span>
            </button>
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Overlay
              className="
                fixed
                inset-0
                bg-black/40
                backdrop-blur-sm
                z-50
                flex
                items-center
                justify-center
                data-[state=open]:animate-overlayShow
                data-[state=closed]:animate-overlayHide
              "
            >
              <Dialog.Content
                className="
                  relative
                  bg-white
                  rounded-xl
                  shadow-2xl
                  w-[90%]
                  md:w-3/4
                  lg:w-2/3
                  max-w-4xl
                  mx-auto
                  p-0
                  data-[state=open]:animate-contentShow
                  data-[state=closed]:animate-contentHide
                "
              >
                <Dialog.Close asChild>
                  <button className="
                    absolute
                    top-1
                    right-1
                    md:top-0
                    md:right-0
                    text-gray-400
                    hover:text-gray-700
                    transition-colors
                    duration-200
                    focus:outline-none
                    p-1
                  ">
                    <X className="h-6 w-6" />
                  </button>
                </Dialog.Close>
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="md:w-1/2 md:block hidden">
                    <img
                      src="https://1.bp.blogspot.com/-WAL3p1DzT20/XRHRd8as7iI/AAAAAAAAA1M/DWQb5G-WtCM0gfRt8PbSzQkdzO1LMr5dgCLcBGAs/s1600/azure-elevation-5349302.jpeg" // Dummy image URL
                      alt="Property Image"
                      className="rounded-lg object-cover w-full h-full"
                    />
                  </div>
                  <div className="md:w-1/2 flex flex-col justify-center p-6">
                    <Dialog.Title className="text-xl md:text-2xl font-extrabold text-gray-900 mt-2 text-center leading-tight">
                      DOWNLOAD BROCHURE
                    </Dialog.Title>
                    <p className="text-gray-600 text-sm text-center mb-4">
                      Fill out the form to download the Godrej Barca brochure.
                    </p>
                    <PropertyInquiryForm onClose={() => setIsBrochureDialogOpen(false)} inquiryType="brochure" />
                  </div>
                </div>
              </Dialog.Content>
            </Dialog.Overlay>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
    </>
  );
}